import { useState, type ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { NewObjectiveDialog } from '@/components/custom/ogsm/new-objective-dialog'
import { Button } from '@/components/ui/button'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import type { ObjectiveDialogTarget } from '@/features/ogsm/new-objective'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import type { OgsmBoardNode } from '@/types/api'

/**
 * "Add strategy" under a band: opens the New objective dialog for a child of
 * `parent`. The hidden-strategies toggle and the `progress` readout share its
 * row, and stay when the board is read-only.
 */
export function AddStrategyRow({
  parent,
  progress,
  inactiveToggle,
}: {
  parent: OgsmBoardNode
  progress: ReactNode
  inactiveToggle?: ReactNode
}) {
  const { companyId, session } = useBoardEdit()
  const [target, setTarget] = useState<ObjectiveDialogTarget | null>(null)
  if (!session.structureEditable) {
    return (
      <div className="flex flex-wrap items-center gap-3 border-t border-t-primary/5 px-3 py-2 empty:hidden lg:col-span-2">
        {inactiveToggle}
        {progress}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-t-primary/5 px-3 py-1.5 lg:col-span-2">
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 text-primary/60"
        onClick={() => setTarget({ mode: 'create-child', parent })}
      >
        <Plus className="size-3" />
        {OGSM_COPY.addStrategy}
      </Button>
      {inactiveToggle}
      {progress}
      <NewObjectiveDialog
        companyId={companyId}
        session={session}
        target={target}
        onClose={() => setTarget(null)}
      />
    </div>
  )
}
